import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql, useFragment} from 'react-relay';
import Styled from 'styled-components';
import {
	Button,
	DropdownList,
	EMPHASIS,
	FlexColumn,
	FlexRow,
	Pill,
	SearchField,
	Text,
	Toggle,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import type {
	BaselineTemplatesList_company$data,
	BaselineTemplatesList_company$key,
} from './__generated__/BaselineTemplatesList_company.graphql';
import {BaselineTemplatesEmptyState} from './BaselineTemplatesEmptyState';
import {HiddenLabel} from './HiddenLabel';
import {DASH, formatDeliverables, formatExpenseLines, formatHours, formatUpdatedAt} from './baselineTemplateFormat';

const COLUMNS = 'minmax(200px, 3fr) 80px 110px 80px 70px 90px 70px 150px 120px 48px';
// Sum of the fixed columns, the 200px minimum of the template column and the nine 12px gaps.
const TABLE_MIN_WIDTH = '1126px';

const Scroll = Styled.div`
	overflow-x: auto;
`;

const Table = Styled.div`
	min-width: ${TABLE_MIN_WIDTH};
`;

const Grid = Styled.div`
	display: grid;
	grid-template-columns: ${COLUMNS};
	gap: 12px;
	align-items: center;
	padding: 12px 0;
	border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};

	> * {
		min-width: 0;
	}
`;

// The intro wraps at a readable line length rather than running the full width of the page.
const Intro = Styled.div`
	max-width: 620px;
`;

// The design-system Toggle always renders its label before the switch, so the filter's text is a
// sibling after a bare Toggle instead. Clicking the text flips the filter like clicking the switch.
const FilterLabel = Styled.span`
	cursor: pointer;
`;

// A name with no spaces (one long word) cannot wrap, so it would spill out of the ellipsis tooltip.
// Letting it break anywhere keeps it inside the box; the cell itself still ends in an ellipsis.
const BreakAnywhere = Styled.span`
	overflow-wrap: anywhere;
`;

// The name shrinks to its ellipsis first, so the Inactive tag beside it always stays whole.
const NameLine = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;

	> :first-child {
		min-width: 0;
	}

	> :not(:first-child) {
		flex-shrink: 0;
	}
`;

const MenuCell = Styled.div`
	position: relative;
`;

const COLUMN_KEYS = ['template', 'phases', 'deliverables', 'hours', 'roles', 'expenses', 'usage', 'created_by', 'last_updated'];
const ROW_ACTIONS = ['edit', 'duplicate', 'deactivate'];

type Template = NonNullable<NonNullable<BaselineTemplatesList_company$data['baselineTemplates']>[number]>;

type Props = {company: BaselineTemplatesList_company$key; onCreate: () => void};

export const BaselineTemplatesList = ({company, onCreate}: Props): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const data = useFragment(
		graphql`
			fragment BaselineTemplatesList_company on Company {
				baselineTemplates {
					id
					name
					description
					includeDeliverables
					active
					usageCount
					phaseCount
					deliverableCount
					totalMinutes
					roleCount
					expenseLineCount
					updatedAt
					createdBy {
						id
						fullName
					}
				}
			}
		`,
		company
	);
	const [query, setQuery] = useState('');
	const [showInactive, setShowInactive] = useState(false);
	const templates = (data.baselineTemplates ?? []).filter((t): t is Template => t !== null);
	const trimmedQuery = query.trim();
	const wanted = trimmedQuery.toLowerCase();
	const inactiveCount = templates.filter(t => t.active === false).length;
	const visible = templates.filter(
		t => (showInactive || t.active !== false) && (!wanted || (t.name ?? '').toLowerCase().includes(wanted))
	);

	return (
		<div data-cy="baseline-templates-list">
			<FlexColumn gap="l">
				<Intro>
					<Text color="auxiliary">{formatMessage({id: 'baseline_templates.list.intro'})}</Text>
				</Intro>
				<FlexRow justifyContent="space-between">
					<FlexRow gap="l" alignItems="center">
						<SearchField
							value={query}
							onChange={setQuery}
							width="280px"
							placeholder={formatMessage({id: 'baseline_templates.list.search'})}
							data-cy="baseline-templates-search"
						/>
						<FlexRow gap="s" alignItems="center">
							<Toggle
								checked={showInactive}
								onChange={() => setShowInactive(!showInactive)}
								cy="baseline-templates-show-inactive"
							/>
							<FilterLabel
								onClick={() => setShowInactive(!showInactive)}
								data-cy="baseline-templates-show-inactive-label"
							>
								<Text>
									{formatMessage({id: 'baseline_templates.list.show_inactive'}, {count: inactiveCount})}
								</Text>
							</FilterLabel>
						</FlexRow>
					</FlexRow>
					<Button emphasis={EMPHASIS.HIGH} icon="plus" onPress={onCreate} data-cy="baseline-templates-create">
						{formatMessage({id: 'baseline_templates.create_template'})}
					</Button>
				</FlexRow>
				{visible.length === 0 ? (
					<BaselineTemplatesEmptyState hasTemplates={templates.length > 0} query={trimmedQuery} />
				) : (
					<Scroll>
						<Table role="table" aria-label={formatMessage({id: 'baseline_templates.tab.baseline'})}>
							<Grid role="row">
								{COLUMN_KEYS.map(column => (
									<div key={column} role="columnheader">
										<Text color="auxiliary">
											{formatMessage({id: `baseline_templates.column.${column}`})}
										</Text>
									</div>
								))}
								<span aria-hidden="true" />
							</Grid>
							{visible.map(template => {
								const menuLabelId = `baseline-template-menu-label-${template.id}`;
								const nameId = `baseline-template-name-${template.id}`;
								return (
									<Grid key={template.id} role="row" data-cy={`baseline-template-row-${template.id}`}>
										<div role="cell">
											<NameLine>
												<div id={nameId}>
													<Text ellipsis tooltipOnEllipsis>
														<BreakAnywhere>{template.name}</BreakAnywhere>
													</Text>
												</div>
												{template.active === false ? (
													<span data-cy="baseline-template-inactive">
														<Pill outlined>{formatMessage({id: 'common.inactive'})}</Pill>
													</span>
												) : null}
											</NameLine>
											{template.description ? (
												<Text color="auxiliary" ellipsis tooltipOnEllipsis>
													<BreakAnywhere>{template.description}</BreakAnywhere>
												</Text>
											) : null}
										</div>
										<div role="cell" data-cy="baseline-template-cell-phases">
											<Text>{template.phaseCount ?? 0}</Text>
										</div>
										<div role="cell" data-cy="baseline-template-cell-deliverables">
											<Text>{formatDeliverables(template)}</Text>
										</div>
										<div role="cell" data-cy="baseline-template-cell-hours">
											<Text>{formatHours(intl, template.totalMinutes ?? 0)}</Text>
										</div>
										<div role="cell" data-cy="baseline-template-cell-roles">
											<Text>{template.roleCount ?? 0}</Text>
										</div>
										<div role="cell" data-cy="baseline-template-cell-expenses">
											<Text>{formatExpenseLines(template)}</Text>
										</div>
										<div role="cell" data-cy="baseline-template-cell-usage">
											<Text>{template.usageCount ?? 0}</Text>
										</div>
										<div role="cell" data-cy="baseline-template-cell-created-by">
											<Text ellipsis tooltipOnEllipsis>
												<BreakAnywhere>{template.createdBy?.fullName || DASH}</BreakAnywhere>
											</Text>
										</div>
										<div role="cell" data-cy="baseline-template-cell-last-updated">
											<Text>{formatUpdatedAt(template.updatedAt)}</Text>
										</div>
										<MenuCell role="cell">
											<HiddenLabel id={menuLabelId}>
												{formatMessage({id: 'baseline_templates.action.more'})}
											</HiddenLabel>
											<DropdownList>
												<DropdownList.Trigger>
													<Button
														emphasis={EMPHASIS.LOW}
														icon="more"
														aria-labelledby={`${menuLabelId} ${nameId}`}
														data-cy="baseline-template-menu"
													/>
												</DropdownList.Trigger>
												<DropdownList.Content align="end" portalled>
													{ROW_ACTIONS.map(action => (
														<DropdownList.Item
															key={action}
															disabled
															data-cy={`baseline-template-action-${action}`}
														>
															{formatMessage({id: `baseline_templates.action.${action}`})}
														</DropdownList.Item>
													))}
												</DropdownList.Content>
											</DropdownList>
										</MenuCell>
									</Grid>
								);
							})}
						</Table>
					</Scroll>
				)}
			</FlexColumn>
		</div>
	);
};
